import React, { useState, useEffect, useRef, useCallback, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Loader2, Pencil, Plus, Trash2, X } from 'lucide-react';
import { API_BASE_URL } from '../config/api';
import { useToast } from '../shared/Toast';
import { SheetField, SheetRow } from '../settings/sheet';
import { useTheme } from '../../contexts/ThemeContext';
import { formatEuro } from '../../utils/formatEuro';
import { loadGoogleMaps } from '../../utils/googleMaps';

/**
 * Ζώνες εξυπηρέτησης των δικών μας διανομέων (Ρυθμίσεις → Delivery, μέσα στην ενότητα «Μέθοδοι
 * Παράδοσης»): χάρτης Google με σχεδίαση πολυγώνου και από κάτω μία γραμμή ανά ζώνη (χρέωση,
 * επεξεργασία, διαγραφή, «Ενεργή»). Από 13/10/2026 στη γλώσσα του «Φύλλου» (settings/sheet): χωρίς
 * δική του κεφαλίδα με εικονίδιο, γραμμές με λεπτή γραμμή ανάμεσα. Ίδια endpoints και δεδομένα με
 * πριν· ο χάρτης ακολουθεί το θέμα (ως τώρα ήταν σκούρος και στο ανοιχτό).
 *
 * API: GET/POST /api/stores/:storeId/delivery-zones, PUT/DELETE …/delivery-zones/:zoneId
 * (routes/deliveryZones.js). Δεν υπάρχει ελάχιστη παραγγελία ανά ζώνη — μόνο επιπλέον χρέωση.
 */

const API_URL = API_BASE_URL;

const getToken = () => localStorage.getItem('xentrix_token');

interface LatLng {
  lat: number;
  lng: number;
}

interface DeliveryZone {
  id: number;
  name: string;
  polygon: LatLng[];
  color: string;
  is_active: boolean;
  delivery_fee: number;
}

interface Props {
  storeId: number;
  storeLat?: number | null;
  storeLng?: number | null;
  googleMapsApiKey?: string | null;
}

const ZONE_COLORS = ['#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6', '#EC4899'];

// Ανοιχτό: ο χάρτης της Google όπως στην αποστολή (DispatchModal). Σκούρο: τα χρώματα που είχε πάντα.
const HIDE_POI_LABELS: google.maps.MapTypeStyle = { featureType: 'poi', elementType: 'labels', stylers: [{ visibility: 'off' }] };
const MAP_STYLES: Record<'light' | 'dark', google.maps.MapTypeStyle[]> = {
  light: [HIDE_POI_LABELS],
  dark: [
    { elementType: 'geometry', stylers: [{ color: '#1e293b' }] },
    { elementType: 'labels.text.stroke', stylers: [{ color: '#1e293b' }] },
    { elementType: 'labels.text.fill', stylers: [{ color: '#94a3b8' }] },
    { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#334155' }] },
    { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#64748b' }] },
    { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#0f172a' }] },
    HIDE_POI_LABELS,
  ],
};

// Όπως στους Διανομείς (delivery/DriversSection) — χωρίς το useAuth του settings/storeSettingsForm.
const INPUT_CLS = 'w-full px-3.5 py-2.5 border border-line-strong rounded-xl bg-surface-2 text-t1 placeholder-t4 '
  + 'focus:outline-none focus:ring-2 focus:ring-ring/50 focus:border-ring text-sm';
const ghostBtn = 'inline-flex items-center justify-center gap-1.5 min-h-[44px] px-4 rounded-xl text-sm font-medium border border-line-strong '
  + 'bg-surface-2 text-t1 hover:bg-surface-3 transition disabled:opacity-50';
const primaryBtn = 'inline-flex items-center justify-center gap-1.5 min-h-[44px] px-4 rounded-xl text-sm font-semibold bg-brand text-brand-fg '
  + 'hover:brightness-110 active:scale-[0.98] transition disabled:opacity-50 disabled:cursor-not-allowed';
// Ήσυχο κουμπί, ίδιο με το «+ Διανομέας».
const quietBtn = 'inline-flex items-center gap-1 min-h-[36px] px-3 rounded-lg border border-line-strong text-t2 hover:bg-surface-3 '
  + 'text-sm font-medium whitespace-nowrap transition-colors disabled:opacity-50';
const iconBtn = 'w-10 h-10 -my-2 rounded-xl flex items-center justify-center transition-colors';

const DeliveryZoneEditor: React.FC<Props> = ({ storeId, storeLat, storeLng, googleMapsApiKey }) => {
  const { t } = useTranslation(['delivery', 'common']);
  const { showToast } = useToast();
  const { resolved: mode } = useTheme();
  const uid = useId();
  const [zones, setZones] = useState<DeliveryZone[]>([]);
  // Ο listener «polygoncomplete» μπαίνει μία φορά στο φόρτωμα του χάρτη και κρατούσε τη ΛΙΣΤΑ ΕΚΕΙΝΗΣ της
  // στιγμής: δεύτερη ζώνη στην ίδια επίσκεψη έπαιρνε ίδιο όνομα και χρώμα με την πρώτη (13/10/2026).
  const zonesRef = useRef(zones);
  zonesRef.current = zones;
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mapReady, setMapReady] = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [editingZone, setEditingZone] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editFee, setEditFee] = useState('');

  const mapRef = useRef<HTMLDivElement>(null);
  const googleMapRef = useRef<google.maps.Map | null>(null);
  const drawingManagerRef = useRef<google.maps.drawing.DrawingManager | null>(null);
  const polygonsRef = useRef<Map<number, google.maps.Polygon>>(new Map());
  const storeMarkerRef = useRef<google.maps.Marker | null>(null);

  const fetchZones = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/stores/${storeId}/delivery-zones`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        const data = await res.json();
        setZones(data.zones || []);
      }
    } catch (err) {
      console.error('Failed to fetch zones:', err);
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    fetchZones();
  }, [fetchZones]);

  // Initialize map
  useEffect(() => {
    if (loading) return;

    loadGoogleMaps(googleMapsApiKey).then(() => {
      if (!mapRef.current || googleMapRef.current) return;

      const center = {
        lat: storeLat || 37.9838,
        lng: storeLng || 23.7275
      };

      const map = new google.maps.Map(mapRef.current, {
        center,
        zoom: 14,
        mapTypeControl: false,
        streetViewControl: false,
        fullscreenControl: true,
        styles: MAP_STYLES[mode],
      });

      // Store marker
      if (storeLat && storeLng) {
        storeMarkerRef.current = new google.maps.Marker({
          position: { lat: storeLat, lng: storeLng },
          map,
          title: t('delivery:yourStore'),
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: '#3B82F6',
            fillOpacity: 1,
            strokeColor: '#ffffff',
            strokeWeight: 3,
          }
        });
      }

      // Drawing manager
      const drawingManager = new google.maps.drawing.DrawingManager({
        drawingMode: null,
        drawingControl: false,
        polygonOptions: {
          fillColor: '#3B82F6',
          fillOpacity: 0.2,
          strokeColor: '#3B82F6',
          strokeWeight: 2,
          editable: true,
          draggable: false,
        }
      });
      drawingManager.setMap(map);
      drawingManagerRef.current = drawingManager;

      // Handle polygon complete
      google.maps.event.addListener(drawingManager, 'polygoncomplete', (polygon: google.maps.Polygon) => {
        const path = polygon.getPath();
        const points: LatLng[] = [];
        for (let i = 0; i < path.getLength(); i++) {
          const p = path.getAt(i);
          points.push({ lat: p.lat(), lng: p.lng() });
        }

        // Remove the drawn polygon (we'll re-render from state)
        polygon.setMap(null);
        drawingManager.setDrawingMode(null);
        setIsDrawing(false);

        // Save to backend
        saveNewZone(points);
      });

      googleMapRef.current = map;
      setMapReady(true);
    });

    return () => {
      // Cleanup polygons
      polygonsRef.current.forEach(p => p.setMap(null));
      polygonsRef.current.clear();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, storeLat, storeLng]);

  // Αλλαγή θέματος με ανοιχτή τη σελίδα: ο χάρτης αλλάζει χρώματα χωρίς να ξαναφτιαχτεί.
  useEffect(() => {
    if (mapReady) googleMapRef.current?.setOptions({ styles: MAP_STYLES[mode] });
  }, [mode, mapReady]);

  // Render polygons on map when zones change
  useEffect(() => {
    if (!googleMapRef.current || !mapReady) return;

    // Clear existing
    polygonsRef.current.forEach(p => p.setMap(null));
    polygonsRef.current.clear();

    zones.forEach(zone => {
      if (!zone.polygon || zone.polygon.length < 3) return;

      const polygon = new google.maps.Polygon({
        paths: zone.polygon,
        fillColor: zone.color || '#3B82F6',
        fillOpacity: zone.is_active ? 0.2 : 0.05,
        strokeColor: zone.color || '#3B82F6',
        strokeOpacity: zone.is_active ? 0.8 : 0.3,
        strokeWeight: 2,
        map: googleMapRef.current!,
      });

      polygonsRef.current.set(zone.id, polygon);
    });
  }, [zones, mapReady]);

  const saveNewZone = async (points: LatLng[]) => {
    setSaving(true);
    try {
      const count = zonesRef.current.length;
      const colorIndex = count % ZONE_COLORS.length;
      const res = await fetch(`${API_URL}/api/stores/${storeId}/delivery-zones`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: t('delivery:zoneDefaultName', { number: count + 1 }),
          polygon: points,
          color: ZONE_COLORS[colorIndex]
        })
      });

      if (res.ok) {
        showToast('success', t('delivery:zoneSaved'));
        fetchZones();
      } else {
        showToast('error', t('delivery:zoneSaveFailed'));
      }
    } catch (err) {
      showToast('error', t('delivery:zoneSaveFailed'));
    } finally {
      setSaving(false);
    }
  };

  // Διαγραφή σε ΔΥΟ πατήματα: το πρώτο οπλίζει («Σίγουρα;»), το δεύτερο διαγράφει.
  // Πριν ένα άστοχο πάτημα στον κάδο έσβηνε αμέσως τη ζώνη (χάρτης, κόστος).
  // Όχι window.confirm — μπλοκάρει στις οθόνες αφής.
  const [armedDeleteId, setArmedDeleteId] = useState<number | null>(null);
  useEffect(() => {
    if (armedDeleteId == null) return;
    const timer = setTimeout(() => setArmedDeleteId(null), 4000);
    return () => clearTimeout(timer);
  }, [armedDeleteId]);

  const deleteZone = async (zoneId: number) => {
    if (armedDeleteId !== zoneId) { setArmedDeleteId(zoneId); return; }
    setArmedDeleteId(null);
    try {
      const res = await fetch(`${API_URL}/api/stores/${storeId}/delivery-zones/${zoneId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      if (res.ok) {
        showToast('success', t('delivery:zoneDeleted'));
        fetchZones();
      } else {
        showToast('error', t('delivery:zoneDeleteFailed'));
      }
    } catch (err) {
      showToast('error', t('delivery:zoneDeleteFailed'));
    }
  };

  const toggleZoneActive = async (zone: DeliveryZone) => {
    try {
      const res = await fetch(`${API_URL}/api/stores/${storeId}/delivery-zones/${zone.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !zone.is_active })
      });
      if (res.ok) {
        fetchZones();
      }
    } catch (err) {
      showToast('error', t('delivery:zoneUpdateFailed'));
    }
  };

  const saveZoneEdit = async (zoneId: number) => {
    if (!editName.trim()) return;
    try {
      const res = await fetch(`${API_URL}/api/stores/${storeId}/delivery-zones/${zoneId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ name: editName.trim(), delivery_fee: parseFloat(editFee) || 0 })
      });
      if (res.ok) {
        setEditingZone(null);
        fetchZones();
      }
    } catch (err) {
      showToast('error', t('delivery:saveFailed'));
    }
  };

  const startEdit = (zone: DeliveryZone) => {
    setEditingZone(zone.id);
    setEditName(zone.name);
    setEditFee(String(zone.delivery_fee || 0));
  };

  const startDrawing = () => {
    if (drawingManagerRef.current) {
      drawingManagerRef.current.setDrawingMode(google.maps.drawing.OverlayType.POLYGON);
      setIsDrawing(true);
    }
  };

  const cancelDrawing = () => {
    if (drawingManagerRef.current) {
      drawingManagerRef.current.setDrawingMode(null);
      setIsDrawing(false);
    }
  };

  // Επεξεργασία στη θέση της γραμμής: όνομα | χρέωση. noValidate: όπως πριν, το Enter αποθηκεύει
  // χωρίς τον έλεγχο step/min του browser.
  const editForm = (zone: DeliveryZone) => (
    <form
      className="py-1"
      onSubmit={e => { e.preventDefault(); saveZoneEdit(zone.id); }}
      onKeyDown={e => { if (e.key === 'Escape') { e.stopPropagation(); setEditingZone(null); } }}
      noValidate
    >
      <div className="xs-grid2">
        <SheetField label={t('delivery:zonesSettings.nameLabel')} htmlFor={`${uid}-name`}>
          <input
            id={`${uid}-name`}
            type="text"
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            autoComplete="off"
            autoFocus
            className={INPUT_CLS}
          />
        </SheetField>
        <SheetField label={t('delivery:zonesSettings.feeLabel')} htmlFor={`${uid}-fee`}>
          <input
            id={`${uid}-fee`}
            type="number"
            step="0.50"
            min="0"
            value={editFee}
            onChange={(e) => setEditFee(e.target.value)}
            placeholder="0.00"
            className={`${INPUT_CLS} tabular-nums`}
          />
        </SheetField>
      </div>
      <div className="mt-4 flex flex-wrap justify-end gap-2">
        <button type="button" onClick={() => setEditingZone(null)} className={ghostBtn}>
          <X className="w-4 h-4" aria-hidden="true" />{t('delivery:zonesSettings.cancel')}
        </button>
        <button type="submit" className={primaryBtn}>
          <Check className="w-4 h-4" aria-hidden="true" />{t('delivery:zonesSettings.save')}
        </button>
      </div>
    </form>
  );

  const zoneRow = (zone: DeliveryZone) => {
    const fee = Number(zone.delivery_fee);
    const armed = armedDeleteId === zone.id;
    const details = [
      fee > 0 ? t('delivery:zonesSettings.fee', { amount: formatEuro(fee) }) : t('delivery:zonesSettings.noFee'),
      `${zone.polygon.length} ${t('delivery:points')}`,
      ...(zone.is_active ? [] : [t('delivery:zonesSettings.inactive')]),
    ].join(' · ');
    return (
      <SheetRow
        title={(
          <span className="flex min-w-0 items-center gap-2.5">
            <span
              aria-hidden="true"
              className="w-3 h-3 shrink-0 rounded-full border-2"
              style={{ backgroundColor: zone.color + '40', borderColor: zone.color }}
            />
            <span className={`min-w-0 truncate ${zone.is_active ? '' : 'text-t3'}`}>{zone.name}</span>
          </span>
        )}
        subtitle={details}
        control={(
          <span className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => startEdit(zone)}
              aria-label={t('delivery:zonesSettings.editAria', { name: zone.name })}
              className={`${iconBtn} text-t3 hover:text-t1 hover:bg-surface-3`}
            >
              <Pencil className="w-4 h-4" aria-hidden="true" />
            </button>
            <button
              type="button"
              onClick={() => deleteZone(zone.id)}
              aria-label={armed
                ? t('delivery:zonesSettings.confirmDeleteAria', { name: zone.name })
                : t('delivery:zonesSettings.deleteAria', { name: zone.name })}
              className={armed
                ? 'inline-flex items-center gap-1.5 h-10 -my-2 px-3 rounded-xl bg-red-600 hover:bg-red-700 text-white text-xs font-semibold whitespace-nowrap transition-colors'
                : `${iconBtn} text-t3 hover:text-red-600 dark:hover:text-red-400 hover:bg-surface-3`}
            >
              <Trash2 className="w-4 h-4" aria-hidden="true" />
              {armed && <span>{t('delivery:zonesSettings.confirmDelete')}</span>}
            </button>
            {/* Όχι SettingsSwitch: το όνομα του διακόπτη λέει και ποια ζώνη αφορά («Ενεργή: …»). */}
            <button
              type="button"
              role="switch"
              aria-checked={zone.is_active}
              aria-label={t('delivery:zonesSettings.activeAria', { name: zone.name })}
              onClick={() => toggleZoneActive(zone)}
              className={`relative ml-2 shrink-0 w-11 h-6 rounded-full transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2 focus-visible:ring-offset-surface-2 ${zone.is_active ? 'bg-brand' : 'bg-surface-4 dark:bg-surface-3'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${zone.is_active ? 'translate-x-5' : ''}`} />
            </button>
          </span>
        )}
      />
    );
  };

  return (
    <div>
      {/* Τίτλος/εξήγηση ως γραμμή του φύλλου, όπως οι διακόπτες από πάνω· «Νέα ζώνη» στη θέση του διακόπτη. */}
      <SheetRow
        title={t('delivery:zonesSettings.title')}
        subtitle={t('delivery:zonesSettings.subtitle')}
        control={isDrawing ? (
          <button type="button" onClick={cancelDrawing} className={quietBtn}>
            <X className="w-4 h-4" aria-hidden="true" />
            {t('delivery:zonesSettings.cancel')}
          </button>
        ) : (
          <button type="button" onClick={startDrawing} disabled={!mapReady} className={quietBtn}>
            <Plus className="w-4 h-4" aria-hidden="true" />
            {t('delivery:zonesSettings.add')}
          </button>
        )}
      />

      {loading ? (
        <div className="mt-4 flex items-center gap-2 text-sm text-t3" role="status">
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />{t('delivery:zonesSettings.loading')}
        </div>
      ) : (
        <>
          {/* Ο χάρτης: γωνίες και γραμμή όπως του φύλλου· στη σχεδίαση η γραμμή παίρνει το χρώμα της εφαρμογής
              και η οδηγία μπαίνει πάνω στον χάρτη (όχι στην εξήγηση: θα κατέβαζε τον χάρτη τη στιγμή του κλικ). */}
          <div className={`relative mt-4 overflow-hidden rounded-2xl border bg-surface-2 transition-colors ${isDrawing ? 'border-brand' : 'border-line'}`}>
            <div ref={mapRef} className="w-full h-[450px]" />
            {isDrawing && (
              <p
                role="status"
                className="pointer-events-none absolute left-1/2 top-3 -translate-x-1/2 w-max max-w-[calc(100%-7rem)] rounded-xl border border-line bg-surface-1/95 px-3 py-1.5 text-center text-xs font-medium text-t1 shadow-sm"
              >
                {t('delivery:zonesSettings.drawing')}
              </p>
            )}
            {saving && (
              <div className="absolute inset-0 flex items-center justify-center bg-surface-1/50">
                <span role="status" className="inline-flex items-center gap-2 rounded-xl border border-line bg-surface-1 px-4 py-2 text-sm font-medium text-t1 shadow-sm">
                  <Loader2 className="w-4 h-4 animate-spin text-t3" aria-hidden="true" />
                  {t('delivery:zonesSettings.saving')}
                </span>
              </div>
            )}
            {!storeLat && !storeLng && (
              <div className="absolute inset-0 flex items-center justify-center bg-surface-1/85 p-6 text-center">
                <div className="max-w-xs">
                  <p className="text-sm font-semibold text-t1">{t('delivery:zonesSettings.noLocation')}</p>
                  <p className="mt-1 text-[13px] leading-[19px] text-t3">{t('delivery:zonesSettings.noLocationHint')}</p>
                </div>
              </div>
            )}
          </div>

          {zones.length > 0 ? (
            // Λεπτή γραμμή ανάμεσα, όπως ανάμεσα σε διαδοχικές γραμμές του φύλλου (sheet.css .xs-row + .xs-row).
            <ul className="mt-5 [&>li+li]:mt-4 [&>li+li]:pt-4 [&>li+li]:border-t [&>li+li]:border-line">
              {zones.map(zone => (
                <li key={zone.id} data-testid={`zone-row-${zone.id}`}>
                  {editingZone === zone.id ? editForm(zone) : zoneRow(zone)}
                </li>
              ))}
            </ul>
          ) : (
            <p className="mt-4 text-sm text-t3">{t('delivery:zonesSettings.empty')}</p>
          )}
          <p className="xs-note">{t('delivery:zonesSettings.note')}</p>
        </>
      )}
    </div>
  );
};

export default DeliveryZoneEditor;
